import Dates from '@cquential/date-utils';
import {WorkScheduleGridPosition, WorkScheduleDeploymentHolder, DateRange, NullableDateRange, RosterData, RosterDay, WorkSchedulePreference} from '@/types/types';
import {WorkSchedulePosition, WorkScheduleAssignment, WorkScheduleGroupLabel, WorkSchedulePositionFunctionRequirement, Deployment} from '@cquential/api';
import {PlanbordWorkSchedulePosition, PlanbordWorkScheduleGroupLabel, CompanyWithDeploymentsAndEmployees, CompanyEmployeeWithDeployments, ResourceWithKeyedByDateObjects, PlanbordResource} from '@/types/api-object-extensions';
import {ResourceUtil} from '@/util/ResourceUtil';

export default class WorkScheduleUtil {
    public static readonly REM_IN_PX: number = 16;
    public static readonly HOURS_CELL_WIDTH_IN_REM: number = 6;
    public static readonly HOURS_CELL_WIDTH_IN_PX: number = WorkScheduleUtil.HOURS_CELL_WIDTH_IN_REM * WorkScheduleUtil.REM_IN_PX;
    public static readonly LABEL_CELL_WIDTH_IN_REM: number = 10;
    public static readonly HOURS_OF_PARTIAL_VISIBLE_DAY: number = 6;
    public static readonly SECONDS_PER_COLUMN: number = Dates.Time.SECONDS_IN_ONE_HOUR;

    public static getPositionInHours(beginDateTimeDeployment: Date, begin: Date, end: Date): WorkScheduleGridPosition {
        const beginInHours: number = (begin.getTime() - beginDateTimeDeployment.getTime()) / (1000 * 3600);
        const endInHours: number = (end.getTime() - beginDateTimeDeployment.getTime()) / (1000 * 3600);

        return {
            left: beginInHours,
            width: endInHours - beginInHours,
        };
    }

    public static getGridPositionInHours(beginDateTime: Date, endDateTime: Date, renderedPeriod: DateRange): WorkScheduleGridPosition {
        const gridBeginDateTime: number = renderedPeriod.begin.getTime();

        const beginDateTimeInHours: number = (beginDateTime.getTime() - gridBeginDateTime) / (1000 * 3600);
        const endDateTimeInHours: number = (endDateTime.getTime() - gridBeginDateTime) / (1000 * 3600);
        const gridLastHour: number = (renderedPeriod.end.getTime() - gridBeginDateTime) / (1000 * 3600);

        return {
            left: beginDateTimeInHours,
            width: Math.min(endDateTimeInHours, gridLastHour) - beginDateTimeInHours,
        };
    }

    public static isDateRangeOverlapping(range: DateRange, min: Date, max: Date): boolean {
        return range.begin < max && range.end > min;
    }

    public static timeBlocksWithoutWorkScheduleAssignments(workScheduleAssignments: WorkScheduleAssignment[], beginDateTime: Date, endDateTime: Date, unavailableTimeRanges: DateRange[] = []): DateRange[] {
        if (workScheduleAssignments.length === 0) {
            return WorkScheduleUtil.filterUnavailableTimeRanges([{begin: beginDateTime, end: endDateTime}], unavailableTimeRanges);
        }

        const beginDateTimes: Date[] = workScheduleAssignments.map((workScheduleAssignment: WorkScheduleAssignment): Date => workScheduleAssignment.beginDateTime);
        const endDateTimes: Date[] = workScheduleAssignments.map((workScheduleAssignment: WorkScheduleAssignment): Date => workScheduleAssignment.endDateTime);
        const timeBlocksWithoutWorkScheduleAssignments: DateRange[] = [];

        if (beginDateTimes[0] > beginDateTime) {
            timeBlocksWithoutWorkScheduleAssignments.push({begin: beginDateTime, end: beginDateTimes[0]});
        }

        for (let i: number = 1; i < beginDateTimes.length; i++) {
            if (endDateTimes[i - 1] < beginDateTimes[i]) {
                timeBlocksWithoutWorkScheduleAssignments.push({begin: endDateTimes[i - 1], end: beginDateTimes[i]});
            }
        }

        if (endDateTimes[endDateTimes.length - 1] < endDateTime) {
            timeBlocksWithoutWorkScheduleAssignments.push({begin: endDateTimes[endDateTimes.length - 1], end: endDateTime});
        }

        return WorkScheduleUtil.filterUnavailableTimeRanges(timeBlocksWithoutWorkScheduleAssignments, unavailableTimeRanges);
    }

    private static filterUnavailableTimeRanges(timeBlocks: DateRange[], unavailableTimeRanges: DateRange[]): DateRange[] {
        const removeIndexes: number[] = [];
        for (const unavailableTimeRange of unavailableTimeRanges) {
            for (const timeBlockIndex in timeBlocks) {
                const timeBlock: DateRange = timeBlocks[timeBlockIndex];
                if (unavailableTimeRange.begin <= timeBlock.begin && unavailableTimeRange.end > timeBlock.begin) {
                    if (unavailableTimeRange.end >= timeBlock.end) {
                        removeIndexes.push(Number(timeBlockIndex));
                    } else {
                        timeBlock.begin = unavailableTimeRange.end;
                    }
                } else if (unavailableTimeRange.begin < timeBlock.end && unavailableTimeRange.end >= timeBlock.end) {
                    timeBlock.end = unavailableTimeRange.begin;
                } else if (unavailableTimeRange.begin > timeBlock.begin && unavailableTimeRange.end < timeBlock.end) {
                    timeBlocks.splice(Number(timeBlockIndex), 0, {begin: timeBlock.begin, end: unavailableTimeRange.begin});
                    timeBlock.begin = unavailableTimeRange.end;
                }
            }
        }

        return timeBlocks.filter((_: DateRange, index: number): boolean => !removeIndexes.includes(index));
    }

    public static convertWorkSchedulePositionToPlanbordWorkSchedulePosition(workSchedulePosition: WorkSchedulePosition): PlanbordWorkSchedulePosition {
        const workScheduleAssignments: WorkScheduleAssignment[] | undefined = workSchedulePosition
            .workScheduleAssignments
            ?.sort((a: WorkScheduleAssignment, b: WorkScheduleAssignment): number => a.beginDateTime.getTime() - b.beginDateTime.getTime());

        const groupedFunctionRequirementsPerGroup: Record<number, WorkSchedulePositionFunctionRequirement[]> = {};
        for (const workSchedulePositionFunctionRequirement of workSchedulePosition.functionRequirements ?? []) {
            groupedFunctionRequirementsPerGroup[workSchedulePositionFunctionRequirement.evaluationGroup] ??= [];
            groupedFunctionRequirementsPerGroup[workSchedulePositionFunctionRequirement.evaluationGroup].push(workSchedulePositionFunctionRequirement);
        }
        const groupedFunctionRequirements: WorkSchedulePositionFunctionRequirement[][] = Object.values(groupedFunctionRequirementsPerGroup);

        return {
            ...workSchedulePosition,
            workScheduleAssignments,
            timeBlocksWithoutWorkScheduleAssignment: WorkScheduleUtil
                .timeBlocksWithoutWorkScheduleAssignments(workScheduleAssignments ?? [], workSchedulePosition.beginDateTime, workSchedulePosition.endDateTime),
            groupedFunctionRequirements: groupedFunctionRequirements.length > 0 ? groupedFunctionRequirements : undefined,
        };
    }

    public static convertDateRangeToRosterDays(dateRange: NullableDateRange, roster: RosterData): RosterDay[] {
        if (dateRange.begin === null || dateRange.end === null) {
            return [];
        }

        const rosterDayRange: RosterDay[] = [];

        let date: Date = dateRange.begin!;
        while (Dates.compare(date).isBeforeOrEqual(dateRange.end!)) {
            const rosterDay: RosterDay | undefined = roster.days.find((rosterDay: RosterDay): boolean => Dates.compare(date).isSameDay(rosterDay.date));
            if (rosterDay) {
                rosterDayRange.push(rosterDay);
            } else {
                throw new Error('No RosterDay for ' + date);
            }
            date = Dates.Utils.addDays(date, 1);
        }

        return rosterDayRange;
    }

    public static convertWorkScheduleGroupLabelToPlanbordWorkScheduleGroupLabel(workScheduleGroupLabel: WorkScheduleGroupLabel): PlanbordWorkScheduleGroupLabel {
        return {
            ...workScheduleGroupLabel,
            workSchedulePositions: {},
        };
    }

    public static isDeploymentHolderACompany(deploymentHolder: WorkScheduleDeploymentHolder): deploymentHolder is CompanyWithDeploymentsAndEmployees {
        return 'deploymentsWithoutEmployee' in deploymentHolder;
    }

    public static isDeploymentHolderACompanyEmployee(deploymentHolder: WorkScheduleDeploymentHolder): deploymentHolder is CompanyEmployeeWithDeployments {
        return 'companyId' in deploymentHolder;
    }

    public static isDeploymentHolderAResource(deploymentHolder: WorkScheduleDeploymentHolder): deploymentHolder is PlanbordResource | ResourceWithKeyedByDateObjects {
        return 'employmentId' in deploymentHolder;
    }

    public static isDeploymentHolderWithDeploymentProperty(deploymentHolder: WorkScheduleDeploymentHolder): deploymentHolder is ResourceWithKeyedByDateObjects | CompanyEmployeeWithDeployments {
        return 'deployments' in deploymentHolder;
    }

    public static getDeploymentHolderDeployments(deploymentHolder: WorkScheduleDeploymentHolder): Record<string, Deployment[]> {
        if (WorkScheduleUtil.isDeploymentHolderACompany(deploymentHolder)) {
            return deploymentHolder.deploymentsWithoutEmployee;
        }

        return deploymentHolder.deployments;
    }

    public static sortEmployees(employees: WorkScheduleDeploymentHolder[], selectedDayIsoFormatted: string, orderBy: WorkSchedulePreference['employeeSorting']): WorkScheduleDeploymentHolder[] {
        return employees.toSorted((a: WorkScheduleDeploymentHolder, b: WorkScheduleDeploymentHolder): number => {
            if (orderBy === 'rosterRank') {
                if ('rankInRoster' in a && 'rankInRoster' in b) {
                    return a.rankInRoster - b.rankInRoster;
                } else if ('rankInRoster' in a) {
                    return -1;
                } else if ('rankInRoster' in b) {
                    return 1;
                } else {
                    return 0;
                }
            }

            const deploymentsA: Deployment[] = WorkScheduleUtil.getDeploymentHolderDeployments(a)[selectedDayIsoFormatted] ?? [];
            const deploymentsB: Deployment[] = WorkScheduleUtil.getDeploymentHolderDeployments(b)[selectedDayIsoFormatted] ?? [];

            if (deploymentsA.length === 0 || deploymentsB.length === 0) {
                return deploymentsB.length - deploymentsA.length; // to sort DeploymentHolders without deployments as last
            }

            const beginTimeA: number = Math.min(...deploymentsA.map((deployment: Deployment): number => deployment.specifications![0]?.begin.getTime() ?? 0)); // DeploymentHolders could have deployments without specifications
            const beginTimeB: number = Math.min(...deploymentsB.map((deployment: Deployment): number => deployment.specifications![0]?.begin.getTime() ?? 0));

            if (beginTimeA === beginTimeB) {
                const nameA: string = ('nameAddendum' in a) ? ResourceUtil.displayName(a as PlanbordResource) : a.name;
                const nameB: string = ('nameAddendum' in b) ? ResourceUtil.displayName(b as PlanbordResource) : b.name;

                return nameA.localeCompare(nameB);
            }

            return beginTimeA - beginTimeB;
        });
    }

    public static employeeSortingCanShowTitle(sorting: WorkSchedulePreference['employeeSorting']): boolean {
        return sorting === 'rosterRank';
    };
}
